import { useEffect, useState } from 'react';
import { siteCorrectionApi } from '../../api';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// Queue of workers who reported "my assigned site is wrong" (SiteCorrectionRequest).
// A claim disappears from here the moment it's resolved — Approve (sets the
// worker's site to what they claimed), Dismiss (closes it without changing
// anything), or a separate site change elsewhere that happens to land on the
// exact site claimed. There's no history view — once resolved, it's gone.
export default function SiteCorrections({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actioning, setActioning] = useState(null); // { id, type: 'approve'|'dismiss' }
  const [confirmTarget, setConfirmTarget] = useState(null); // { id, type, workerName, siteName }

  const load = () => {
    setLoading(true);
    siteCorrectionApi.list()
      .then((r) => setRows(r.data.data || []))
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const runAction = async () => {
    const { id, type } = confirmTarget;
    setActioning({ id, type });
    setError('');
    try {
      await (type === 'approve' ? siteCorrectionApi.approve(id) : siteCorrectionApi.dismiss(id));
      setConfirmTarget(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${type}`);
    }
    setActioning(null);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {!embedded && (
        <PageHeader
          title="Site Corrections"
          subtitle="Workers who reported their assigned site is wrong, awaiting review"
        />
      )}

      {error && (
        <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</p>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-12">No pending site corrections.</p>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Worker</th>
                <th>Current Site</th>
                <th>Claims Their Site Is</th>
                <th>Raised</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const currentSites = (row.worker.siteAssignments || []).map((a) => a.site?.name).filter(Boolean);
                return (
                  <tr key={row.id}>
                    <td>
                      <p className="text-sm font-medium text-gray-900">{row.worker.name}</p>
                      <p className="text-xs text-gray-400 font-mono">{row.worker.employeeId}</p>
                    </td>
                    <td className="text-sm text-gray-600">{currentSites.join(', ') || '— none —'}</td>
                    <td className="text-sm font-medium text-indigo-700">{row.claimedSite?.name}</td>
                    <td className="text-sm text-gray-500">{fmtDate(row.raisedAt)}</td>
                    <td className="whitespace-nowrap">
                      <button
                        className="text-green-600 hover:text-green-700 text-sm font-medium mr-3"
                        onClick={() => setConfirmTarget({ id: row.id, type: 'approve', workerName: row.worker.name, siteName: row.claimedSite?.name })}
                      >
                        Approve
                      </button>
                      <button
                        className="text-red-500 hover:text-red-600 text-sm font-medium"
                        onClick={() => setConfirmTarget({ id: row.id, type: 'dismiss', workerName: row.worker.name, siteName: row.claimedSite?.name })}
                      >
                        Dismiss
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmTarget}
        title={confirmTarget?.type === 'approve' ? 'Approve Site Correction' : 'Dismiss Claim'}
        message={
          confirmTarget?.type === 'approve'
            ? `Set ${confirmTarget?.workerName}'s site to ${confirmTarget?.siteName}?`
            : `Dismiss ${confirmTarget?.workerName}'s claim without changing their site?`
        }
        confirmLabel={confirmTarget?.type === 'approve' ? 'Approve' : 'Dismiss'}
        danger={confirmTarget?.type === 'dismiss'}
        onConfirm={runAction}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}
